'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Navbar from '@/components/Navbar';
import {
  Settings,
  User,
  Bell,
  Shield,
  Palette,
  KeyRound,
  Mail,
  Trash2,
  LogOut,
  Save,
  Eye,
  EyeOff,
  AlertTriangle,
  X,
  Check,
  Globe,
} from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/lib/i18n/LanguageContext';

// ─── Toast ────────────────────────────────────────────────────────────────────
interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}

let toastId = 0;

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: number) => void }) {
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium shadow-xl pointer-events-auto animate-fade-in"
          style={{
            background: t.type === 'success' ? 'var(--bullish-muted)' : 'var(--bearish-muted)',
            border: `1px solid ${t.type === 'success' ? 'var(--bullish)' : 'var(--bearish)'}`,
            color: t.type === 'success' ? 'var(--bullish)' : 'var(--bearish)',
          }}
        >
          {t.type === 'success' ? <Check size={15} /> : <AlertTriangle size={15} />}
          {t.message}
          <button onClick={() => onRemove(t.id)} className="ml-1 opacity-60 hover:opacity-100">
            <X size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Toggle Switch ─────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="relative inline-flex items-center shrink-0 rounded-full transition-all duration-200 focus:outline-none"
      style={{
        width: 44,
        height: 24,
        background: checked ? 'var(--accent)' : 'var(--border)',
        border: `1px solid ${checked ? 'var(--accent-hover)' : 'var(--border)'}`,
        boxShadow: checked ? '0 0 12px var(--accent-glow)' : 'none',
      }}
    >
      <span
        className="inline-block rounded-full bg-white transition-transform duration-200"
        style={{
          width: 18,
          height: 18,
          transform: checked ? 'translateX(22px)' : 'translateX(2px)',
          boxShadow: '0 1px 4px rgba(28,43,38,0.3)',
        }}
      />
    </button>
  );
}

// ─── Section Card ──────────────────────────────────────────────────────────────
function SectionCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card-base overflow-hidden">
      <div
        className="flex items-center gap-3 px-6 py-4 border-b"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <span style={{ color: 'var(--accent)' }}>{icon}</span>
        <h2 className="text-base font-heading font-bold" style={{ color: 'var(--text-primary)' }}>
          {title}
        </h2>
      </div>
      <div className="px-6 py-5 flex flex-col gap-5">{children}</div>
    </div>
  );
}

// ─── Delete Confirmation Dialog ────────────────────────────────────────────────
function DeleteDialog({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  const { t } = useLang();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(28,43,38,0.55)', backdropFilter: 'blur(4px)' }}>
      <div className="card-base w-full max-w-sm p-6 flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--bearish-muted)' }}>
            <Trash2 size={18} style={{ color: 'var(--bearish)' }} />
          </div>
          <div>
            <h3 className="font-heading font-bold" style={{ color: 'var(--text-primary)' }}>{t('Delete Account')}</h3>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('This action cannot be undone')}</p>
          </div>
        </div>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          {t('All your trades, followers, and data will be permanently deleted. Are you absolutely sure?')}
        </p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="btn-ghost flex-1 py-2.5 text-sm">
            {t('Cancel')}
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors"
            style={{ background: 'var(--bearish-muted)', color: 'var(--bearish)', border: '1px solid var(--bearish)' }}
          >
            {t('Delete Account')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Settings Page ────────────────────────────────────────────────────────
// Rendered inside the owner's profile as the "Podešavanja" tab (embedded) — /settings only
// redirects there now (owner request 2026-09-02: one page for profile + settings).
export default function SettingsPanel({ embedded = false }: { embedded?: boolean }) {
  const { user, accessToken, signOut, loading: authLoading } = useAuth();
  const router = useRouter();
  const { lang, setLang, t } = useLang();

  // Auth guard – redirect unauthenticated visitors
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const wasAuth = typeof window !== 'undefined' && localStorage.getItem('smt_auth_active') === '1';
      if (!wasAuth) {
        router.replace('/sign-up-login-screen');
      }
    }
  }, [authLoading, user, router]);

  const [toasts, setToasts] = useState<Toast[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Account
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [signOutLoading, setSignOutLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Notifications
  const [notifLikes, setNotifLikes] = useState(true);
  const [notifComments, setNotifComments] = useState(true);
  const [notifFollows, setNotifFollows] = useState(true);
  const [notifEmailSignalUpdates, setNotifEmailSignalUpdates] = useState(true);
  const [notifEmailWeekly, setNotifEmailWeekly] = useState(true);
  const [notifLoading, setNotifLoading] = useState(false);

  // Privacy
  const [profilePublic, setProfilePublic] = useState(true);
  const [showTradeStats, setShowTradeStats] = useState(true);
  const [privacyLoading, setPrivacyLoading] = useState(false);

  // Appearance
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');

  // Profile
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  // Contact card ("Moj profil"): name + phone are shown to the owner, social links publicly.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [socials, setSocials] = useState<Record<string, string>>({ telegram: '', instagram: '', twitter: '', youtube: '', tiktok: '', linkedin: '' });
  const [avatarUrl, setAvatarUrl] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // ── Toast helpers ──
  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Load settings ──
  useEffect(() => {
    const storedTheme = (localStorage.getItem('theme') as 'dark' | 'light') ?? 'dark';
    setTheme(storedTheme);
    document.documentElement.setAttribute('data-theme', storedTheme);
  }, []);

  useEffect(() => {
    if (!user || settingsLoaded) return;
    const fetchSettings = async () => {
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
        const res = await fetch('/api/settings', { headers });
        if (!res.ok) return;
        const { settings } = await res.json();
        if (!settings) return;
        setUsername(settings.username ?? '');
        setBio(settings.bio ?? '');
        setFirstName(settings.firstName ?? '');
        setLastName(settings.lastName ?? '');
        setPhone(settings.phone ?? '');
        setSocials({
          telegram: settings.telegram ?? '', instagram: settings.instagram ?? '', twitter: settings.twitter ?? '',
          youtube: settings.youtube ?? '', tiktok: settings.tiktok ?? '', linkedin: settings.linkedin ?? '',
        });
        setAvatarUrl(settings.image ?? '');
        setNotifLikes(settings.notifLikes ?? true);
        setNotifComments(settings.notifComments ?? true);
        setNotifFollows(settings.notifFollows ?? true);
        setNotifEmailSignalUpdates(settings.notifEmailSignalUpdates ?? true);
        setNotifEmailWeekly(settings.notifEmailWeekly ?? true);
        setProfilePublic(settings.profilePublic ?? true);
        setShowTradeStats(settings.showTradeStats ?? true);
        setSettingsLoaded(true);
      } catch {
        // silent
      }
    };
    fetchSettings();
  }, [user, accessToken, settingsLoaded]);

  // ── Handlers ──
  const handleUpdateEmail = async () => {
    if (!newEmail.trim()) return addToast('error', t('Please enter a new email address.'));
    setEmailLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail.trim() });
      if (error) throw error;
      addToast('success', t('Confirmation email sent. Check your inbox.'));
      setNewEmail('');
    } catch (err: any) {
      addToast('error', t(err.message ?? 'Failed to update email.'));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword) return addToast('error', t('Please enter a new password.'));
    if (newPassword.length < 6) return addToast('error', t('Password must be at least 6 characters.'));
    if (newPassword !== confirmPassword) return addToast('error', t('Passwords do not match.'));
    setPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      addToast('success', t('Password updated successfully.'));
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      addToast('error', t(err.message ?? 'Failed to update password.'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSignOut = async () => {
    setSignOutLoading(true);
    try {
      await signOut();
      router.push('/sign-up-login-screen');
    } catch (err: any) {
      addToast('error', t(err.message ?? 'Failed to sign out.'));
      setSignOutLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setShowDeleteDialog(false);
    setDeleteLoading(true);
    try {
      const { error } = await supabase.rpc('delete_user');
      if (error) throw error;
      await signOut();
      router.push('/sign-up-login-screen');
    } catch (err: any) {
      addToast('error', t('Account deletion failed. Please contact support.'));
      setDeleteLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    setNotifLoading(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      const res = await fetch('/api/settings/notifications', {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ notifLikes, notifComments, notifFollows, notifEmailSignalUpdates, notifEmailWeekly }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? 'Failed');
      addToast('success', t('Notification preferences saved.'));
    } catch (err: any) {
      addToast('error', t(err.message ?? 'Failed to save notifications.'));
    } finally {
      setNotifLoading(false);
    }
  };

  const handleSavePrivacy = async () => {
    setPrivacyLoading(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      const res = await fetch('/api/settings/privacy', {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ profilePublic, showTradeStats }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? 'Failed');
      addToast('success', t('Privacy settings saved.'));
    } catch (err: any) {
      addToast('error', t(err.message ?? 'Failed to save privacy settings.'));
    } finally {
      setPrivacyLoading(false);
    }
  };

  const handleThemeToggle = (val: 'dark' | 'light') => {
    setTheme(val);
    localStorage.setItem('theme', val);
    document.documentElement.setAttribute('data-theme', val);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return addToast('error', t('Image must be under 5MB.'));
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      return addToast('error', t('Use JPG, PNG, or WebP format.'));
    }

    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const headers: Record<string, string> = {};
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      const res = await fetch('/api/settings/avatar', {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error ?? 'Upload failed');
      }
      const data = await res.json();
      setAvatarUrl(data.avatarUrl);
      addToast('success', t('Profile photo updated!'));
    } catch (err: any) {
      addToast('error', t(err.message ?? 'Failed to upload photo.'));
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!username.trim()) return addToast('error', t('Username cannot be empty.'));
    setProfileLoading(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      const res = await fetch('/api/settings/profile', {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          username: username.trim(),
          bio: bio.trim(),
          image: avatarUrl.trim() || null,
          firstName: firstName.trim() || null,
          lastName: lastName.trim() || null,
          phone: phone.trim() || null,
          telegram: socials.telegram.trim() || null,
          instagram: socials.instagram.trim() || null,
          twitter: socials.twitter.trim() || null,
          youtube: socials.youtube.trim() || null,
          tiktok: socials.tiktok.trim() || null,
          linkedin: socials.linkedin.trim() || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? 'Failed');
      addToast('success', t('Profile updated successfully.'));
    } catch (err: any) {
      addToast('error', t(err.message ?? 'Failed to update profile.'));
    } finally {
      setProfileLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    background: 'var(--bg-sunken)',
    border: '1.5px solid var(--border-input)',
    borderRadius: 12,
    color: 'var(--text-primary)',
    padding: '10px 14px',
    fontSize: 14,
    outline: 'none',
    width: '100%',
    fontFamily: "'Instrument Sans', sans-serif",
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--text-secondary)',
    marginBottom: 6,
    display: 'block',
  };

  const focusInput = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = 'var(--accent)';
    e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-muted)';
  };
  const blurInput = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = 'var(--border-input)';
    e.currentTarget.style.boxShadow = 'none';
  };

  const btnPrimary = (loading: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 18px',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 700,
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.6 : 1,
    background: 'var(--accent)',
    color: 'var(--bg-base)',
    border: 'none',
    transition: 'background-color 150ms ease, box-shadow 150ms ease',
  } as React.CSSProperties);

  return (
    <div className={embedded ? '' : 'min-h-screen'} style={embedded ? undefined : { background: 'var(--bg-base)' }}>
      {!embedded && <Navbar />}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {showDeleteDialog && (
        <DeleteDialog
          onConfirm={handleDeleteAccount}
          onCancel={() => setShowDeleteDialog(false)}
        />
      )}

      <div className={embedded ? 'flex flex-col gap-6' : 'max-w-[720px] mx-auto px-4 py-8 pb-24 md:pb-8 flex flex-col gap-6'}>
        {/* Page header (standalone route only) */}
        {!embedded && <div className="flex items-center gap-3 mb-2">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'var(--accent-muted)', border: '1px solid var(--border)' }}
          >
            <Settings size={20} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h1 className="text-xl font-heading font-extrabold" style={{ color: 'var(--text-primary)' }}>{t('Settings')}</h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('Manage your account and preferences')}</p>
          </div>
        </div>}

        {/* ── 1. ACCOUNT ─────────────────────────────────────────────────── */}
        <SectionCard icon={<User size={18} />} title={t('Account')}>
          {/* Current email */}
          <div>
            <span style={labelStyle}>{t('Current Email')}</span>
            <div
              className="px-4 py-2.5 rounded-xl text-sm"
              style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
            >
              {user?.email ?? '—'}
            </div>
          </div>

          {/* Change email */}
          <div className="flex flex-col gap-2">
            <span style={labelStyle}>{t('Change Email')}</span>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder={t('New email address')}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                style={inputStyle}
                onFocus={focusInput}
                onBlur={blurInput}
              />
              <button
                onClick={handleUpdateEmail}
                disabled={emailLoading}
                style={{ ...btnPrimary(emailLoading), whiteSpace: 'nowrap' }}
              >
                {emailLoading ? (
                  <span className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--bg-base)', borderTopColor: 'transparent' }} />
                ) : (
                  <Mail size={14} />
                )}
                {t('Update Email')}
              </button>
            </div>
          </div>

          {/* Change password */}
          <div className="flex flex-col gap-2">
            <span style={labelStyle}>{t('Change Password')}</span>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('New password')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{ ...inputStyle, paddingRight: 40 }}
                onFocus={focusInput}
                onBlur={blurInput}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--text-muted)' }}
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={t('Confirm new password')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={inputStyle}
              onFocus={focusInput}
              onBlur={blurInput}
            />
            <div>
              <button
                onClick={handleUpdatePassword}
                disabled={passwordLoading}
                style={btnPrimary(passwordLoading)}
              >
                {passwordLoading ? (
                  <span className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--bg-base)', borderTopColor: 'transparent' }} />
                ) : (
                  <KeyRound size={14} />
                )}
                {t('Update Password')}
              </button>
            </div>
          </div>

          {/* Danger zone */}
          <div
            className="rounded-xl p-4 flex flex-col gap-3"
            style={{ background: 'var(--bearish-muted)', border: '1px solid var(--bearish)' }}
          >
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--bearish)' }}>
              {t('Danger Zone')}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleSignOut}
                disabled={signOutLoading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                style={{
                  background: 'var(--bg-card)',
                  color: 'var(--bearish)',
                  border: '1px solid var(--bearish)',
                  opacity: signOutLoading ? 0.6 : 1,
                  cursor: signOutLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {signOutLoading ? (
                  <span className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--bearish)', borderTopColor: 'transparent' }} />
                ) : (
                  <LogOut size={14} />
                )}
                {t('Sign Out')}
              </button>
              <button
                onClick={() => setShowDeleteDialog(true)}
                disabled={deleteLoading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                style={{
                  background: 'var(--bg-card)',
                  color: 'var(--bearish)',
                  border: '1px solid var(--bearish)',
                  opacity: deleteLoading ? 0.6 : 1,
                  cursor: deleteLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {deleteLoading ? (
                  <span className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--bearish)', borderTopColor: 'transparent' }} />
                ) : (
                  <Trash2 size={14} />
                )}
                {t('Delete Account')}
              </button>
            </div>
          </div>
        </SectionCard>

        {/* ── 2. NOTIFICATION PREFERENCES ────────────────────────────────── */}
        <SectionCard icon={<Bell size={18} />} title={t('Notification Preferences')}>
          {[
            { label: 'Likes on my trades', value: notifLikes, setter: setNotifLikes },
            { label: 'Comments on my trades', value: notifComments, setter: setNotifComments },
            { label: 'New followers', value: notifFollows, setter: setNotifFollows },
            { label: 'Email me when a trader I follow changes SL/TP on a signal', value: notifEmailSignalUpdates, setter: setNotifEmailSignalUpdates },
            { label: 'Weekly email: top signals of the week', value: notifEmailWeekly, setter: setNotifEmailWeekly },
          ].map(({ label, value, setter }) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-sm font-medium" style={{ color: 'var(--text-body)' }}>{t(label)}</span>
              <Toggle checked={value} onChange={setter} />
            </div>
          ))}
          <div>
            <button onClick={handleSaveNotifications} disabled={notifLoading} style={btnPrimary(notifLoading)}>
              {notifLoading ? (
                <span className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--bg-base)', borderTopColor: 'transparent' }} />
              ) : (
                <Save size={14} />
              )}
              {t('Save Preferences')}
            </button>
          </div>
        </SectionCard>

        {/* ── 3. PRIVACY CONTROLS ────────────────────────────────────────── */}
        <SectionCard icon={<Shield size={18} />} title={t('Privacy Controls')}>
          {[
            { label: 'Public profile', desc: 'Allow anyone to view your profile', value: profilePublic, setter: setProfilePublic },
            { label: 'Show trade statistics', desc: 'Display win rate and performance metrics', value: showTradeStats, setter: setShowTradeStats },
          ].map(({ label, desc, value, setter }) => (
            <div key={label} className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-body)' }}>{t(label)}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{t(desc)}</p>
              </div>
              <Toggle checked={value} onChange={setter} />
            </div>
          ))}
          <div>
            <button onClick={handleSavePrivacy} disabled={privacyLoading} style={btnPrimary(privacyLoading)}>
              {privacyLoading ? (
                <span className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--bg-base)', borderTopColor: 'transparent' }} />
              ) : (
                <Save size={14} />
              )}
              {t('Save Privacy')}
            </button>
          </div>
        </SectionCard>

        {/* ── 4. APPEARANCE ──────────────────────────────────────────────── */}
        <SectionCard icon={<Palette size={18} />} title={t('Appearance')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--text-body)' }}>{t('Theme')}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {theme === 'light' ? (
                  <span style={{ color: 'var(--win)' }}>{t('☀️ Light mode — Coming soon')}</span>
                ) : (
                  t('Dark mode active')
                )}
              </p>
            </div>
            <div
              className="flex items-center rounded-xl overflow-hidden"
              style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)', padding: 3, gap: 2 }}
            >
              {(['dark', 'light'] as const).map((th) => (
                <button
                  key={th}
                  onClick={() => handleThemeToggle(th)}
                  className="px-4 py-1.5 rounded-lg text-xs font-bold transition-all duration-150 capitalize"
                  style={{
                    background: theme === th ? 'var(--accent)' : 'transparent',
                    color: theme === th ? 'var(--bg-base)' : 'var(--text-muted)',
                  }}
                >
                  {t(th)}
                </button>
              ))}
            </div>
          </div>
        </SectionCard>

        {/* ── 4b. LANGUAGE / JEZIK ──────────────────────────────────────── */}
        <SectionCard icon={<Globe size={18} />} title="Language / Jezik">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--text-body)' }}>{t('Language')}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {lang === 'sr' ? 'Srpski je aktivan' : 'English active'}
              </p>
            </div>
            <div
              className="flex items-center rounded-xl overflow-hidden"
              style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)', padding: 3, gap: 2 }}
            >
              {([['sr', 'Srpski'], ['en', 'English']] as const).map(([code, label]) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  className="px-4 py-1.5 rounded-lg text-xs font-bold transition-all duration-150"
                  style={{
                    background: lang === code ? 'var(--accent)' : 'transparent',
                    color: lang === code ? 'var(--bg-base)' : 'var(--text-muted)',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </SectionCard>

        {/* ── 5. PROFILE SETTINGS ────────────────────────────────────────── */}
        <SectionCard icon={<User size={18} />} title={t('Profile Settings')}>
          <div className="flex flex-col gap-2">
            <label style={labelStyle}>{t('Username')}</label>
            <input
              type="text"
              placeholder={t('Your username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={inputStyle}
              onFocus={focusInput}
              onBlur={blurInput}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label style={labelStyle}>{t('Bio')}</label>
            <textarea
              placeholder={t('Tell traders about yourself…')}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
              onFocus={focusInput}
              onBlur={blurInput}
            />
          </div>
          {/* Name + phone (contact card on "Moj profil" — phone visible only to you) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <label style={labelStyle}>{t('First name')}</label>
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} style={inputStyle} onFocus={focusInput} onBlur={blurInput} autoComplete="given-name" />
            </div>
            <div className="flex flex-col gap-2">
              <label style={labelStyle}>{t('Last name')}</label>
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} style={inputStyle} onFocus={focusInput} onBlur={blurInput} autoComplete="family-name" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label style={labelStyle}>{t('Phone')} <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>· {t('visible only to you')}</span></label>
            <input type="tel" placeholder="+381 6x xxx xxxx" value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} onFocus={focusInput} onBlur={blurInput} autoComplete="tel" />
          </div>
          <div className="flex flex-col gap-2">
            <label style={labelStyle}>{t('Social links')} <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>· {t('shown publicly on your profile')}</span></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {([
                ['telegram', 'Telegram', '@korisnik'],
                ['instagram', 'Instagram', '@korisnik'],
                ['twitter', 'X / Twitter', '@korisnik'],
                ['youtube', 'YouTube', '@kanal ili link'],
                ['tiktok', 'TikTok', '@korisnik'],
                ['linkedin', 'LinkedIn', 'korisnik ili link'],
              ] as const).map(([key, label, ph]) => (
                <div key={key} className="flex flex-col gap-1">
                  <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{label}</span>
                  <input
                    type="text"
                    placeholder={ph}
                    value={socials[key]}
                    onChange={(e) => setSocials((s) => ({ ...s, [key]: e.target.value }))}
                    style={inputStyle}
                    onFocus={focusInput}
                    onBlur={blurInput}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label style={labelStyle}>{t('Profile Photo')}</label>
            <div className="flex items-center gap-4">
              {/* Current avatar preview */}
              <div className="relative shrink-0">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Profile"
                    className="w-20 h-20 rounded-2xl object-cover"
                    style={{ border: '2px solid var(--border)' }}
                  />
                ) : (
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl font-bold"
                    style={{ background: 'var(--accent)', color: 'var(--bg-base)', border: '2px solid var(--border)' }}
                  >
                    {username?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                {avatarUploading && (
                  <div className="absolute inset-0 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(28,43,38,0.55)' }}>
                    <span className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'white', borderTopColor: 'transparent' }} />
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all duration-150"
                  style={{ background: 'var(--accent-muted)', color: 'var(--accent)', border: '1px solid transparent' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  {avatarUploading ? t('Uploading...') : t('Upload Photo')}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleAvatarUpload}
                    disabled={avatarUploading}
                    className="hidden"
                  />
                </label>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('JPG, PNG or WebP. Max 5MB.')}</p>
              </div>
            </div>
          </div>
          <div>
            <button onClick={handleSaveProfile} disabled={profileLoading} style={btnPrimary(profileLoading)}>
              {profileLoading ? (
                <span className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--bg-base)', borderTopColor: 'transparent' }} />
              ) : (
                <Save size={14} />
              )}
              {t('Save Profile')}
            </button>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
